<template>
  <section class="account-details">
    <div v-if="userDetails" class="card header-background">
      <span class="users-details">
        <user-avatar class="avatar" />

        <span class="col">
          <span class="row">
            <span v-if="isNameProvided" class="name">
              {{ userDetails.firstName }}
              {{ userDetails.lastName }}
            </span>
            <a class="edit-link" @click.prevent="userEditNameVisibility = true"
              ><img
                src="@/assets/user-account/edit.svg"
                width="21"
                height="19"
                alt="" /></a
          ></span>
          <span class="username">{{ userDetails.username }}</span>
          <span class="row">
            <div class="col">
              <div class="useremail">{{ userEmail }}</div>
              <div class="date">
                <i18n-t keypath="Member since {date}">
                  <template #date>
                    <pu-date
                      v-if="userDetails.dateJoined"
                      class="value"
                      :date="userDetails.dateJoined"
                      :options="{ month: 'long', year: 'numeric' }"
                    />
                  </template>
                </i18n-t>
              </div>
            </div>
            <a
              class="edit-link email"
              @click.prevent="emailEditVisibility = true"
              ><img
                src="@/assets/user-account/edit.svg"
                width="21"
                height="19"
                alt="" /></a
          ></span>
          <floating-window v-if="userEditNameVisibility">
            <user-details-form @close="userEditNameVisibility = false" />
          </floating-window>
          <floating-window v-if="emailEditVisibility">
            <change-email-main @cancel="emailEditVisibility = false" />
          </floating-window>
        </span>
      </span>
      <hr />
      <span class="button-row">
        <button class="account-action" @click.prevent="doLogout">
          <translate> Log out </translate>
        </button>
        <button
          class="account-action"
          @click.prevent="passwordEditVisibility = true"
        >
          <translate>Change password</translate>
        </button>
      </span>
    </div>
    <floating-window v-if="passwordEditVisibility">
      <user-change-password
        @submit="passwordEditVisibility = false"
        @cancel="passwordEditVisibility = false"
        :user-email="userEmail"
      />
    </floating-window>

    <personal-details />
    <user-stored-cards />

    <section class="meta">
      <table v-if="userDetails.lastLogin?.timestamp">
        <tbody>
          <tr>
            <translate tag="th">Last login:</translate>
            <td>
              <PuDate
                :date="userDetails.lastLogin.timestamp"
                options="fullDateTime"
              />
            </td>
          </tr>
          <tr
            v-if="
              userDetails.lastLogin?.meta?.location?.region &&
              userDetails.lastLogin?.meta?.location?.countryName
            "
          >
            <translate tag="th">Location:</translate>
            <td>
              {{ userDetails.lastLogin?.meta?.location?.region }},
              {{ userDetails.lastLogin?.meta?.location?.countryName }}.
            </td>
          </tr>
          <tr v-if="userDetails.lastLogin?.deviceInfo">
            <translate tag="th">Device:</translate>
            <td>
              <translate
                :tt="{
                  browser: userDetails.lastLogin?.deviceInfo?.browser,
                  device: userDetails.lastLogin?.deviceInfo?.device,
                }"
              >
                {browser} on {device}
              </translate>
              <pu-popover v-if="userDetails.lastLogin?.meta">
                <template #content>
                  <table>
                    <tbody>
                      <tr
                        v-for="[key, value] in Object.entries(
                          userDetails.lastLogin?.meta?.location || {},
                        )"
                        :key="key"
                      >
                        <th>{{ key }}</th>
                        <td>{{ value }}</td>
                      </tr>
                      <tr>
                        <th>User agent</th>
                        <td>{{ userDetails.lastLogin?.meta?.userAgent }}</td>
                      </tr>
                    </tbody>
                  </table>
                </template>
              </pu-popover>
            </td>
          </tr>
        </tbody>
      </table>

      <i18n-t
        keypath="Last password change {date}"
        tag="p"
        v-if="userDetails.lastPasswordChange?.timestamp"
      >
        <template #date>
          <pu-date
            :date="userDetails.lastPasswordChange.timestamp"
            :options="{ month: 'long', year: 'numeric' }"
          />
        </template>
      </i18n-t>
    </section>

    <!-- <section class="meta">
      <div class="row"> -->
    <!-- @TODO add 'are you sure' check, and help info -->
    <!-- <a @click.prevent="userDetailsStore.deactivateAccount"
          >Deactivate account</a
        > -->
    <!-- @TODO -->
    <!-- <a>Set up 2 factor authentication</a>
      </div>
    </section> -->
  </section>
</template>

<script setup lang="ts">
const userEditNameIsVisible = ref(false)
const emailEditIsVisible = ref(false)
const passwordEditIsVisible = ref(false)

const userEditNameVisibility = computed({
  get: () => userEditNameIsVisible.value,
  set: (value) => {
    setVisibility(userEditNameIsVisible, value)
  },
})

const emailEditVisibility = computed({
  get: () => emailEditIsVisible.value,
  set: (value) => {
    setVisibility(emailEditIsVisible, value)
  },
})

const passwordEditVisibility = computed({
  get: () => passwordEditIsVisible.value,
  set: (value) => {
    setVisibility(passwordEditIsVisible, value)
  },
})

const userDetailsStore = useUserDataStore(usePinia())
const userDetails = computed(
  () =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- TODO: Type useUserDataStore.userDetails properly
    userDetailsStore.userDetails,
)

const authStore = useAuthStore()
const userEmail = computed(() => authStore.userEmail)

/* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access -- TODO: Type userDetails properly */
const isNameProvided = computed(
  () => userDetails.value?.firstName || userDetails.value?.lastName,
)
/* eslint-enable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access */

function setVisibility(ref: Ref<boolean>, value: boolean) {
  ref.value = value
}

const { $puLocalePath } = useNuxtApp()
function doLogout() {
  authStore.logOut($puLocalePath({ name: 'index' }))
}
</script>

<style scoped>
.account-details {
  min-height: 100dvh;
  padding: 0;
}

.card.header-background {
  margin-top: var(--edge-margin) !important;
  margin-bottom: 0.9em;
}

.header-background::after {
  left: calc(var(--edge-margin) * -1);
}

.users-details .col {
  width: 100%;
}

.users-details .row {
  justify-content: space-between;
}

.edit-link {
  opacity: 0.75;
  flex-shrink: 0;
}

.edit-link.email {
  padding-top: 0.8em;
}

.meta {
  margin: 0 var(--edge-margin) 1.5em;
  padding: 0;
}

.meta td,
.meta th,
.meta p {
  font-size: var(--small);
  color: var(--colour-text-light);
  margin-top: 0;
  line-height: 1.5;
}

.meta table {
  border-top: 1px solid var(--colour-page-background-light);
}

.meta th {
  white-space: nowrap;
  min-width: fit-content;
}

.meta td {
  width: 100%;
}

.meta tr {
  border-color: var(--colour-page-background-light);
}

.meta a {
  font-size: var(--body);
}

.meta .row {
  justify-content: space-between;
  margin-top: 0.35em;
}

.users-details {
  display: grid;
  grid-template-columns: 2.3em 1fr;
  place-items: start start;
  gap: 0.7em;
  margin: 0 0 0.7em 0;
}

:deep(.avatar) {
  display: inline-block;
  height: 2.3em;
  width: 2.3em;
  margin: 0;
  background: var(--colour-mid-green);
}

.col {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.name {
  font-size: var(--large);
  color: var(--colour-dark-blue);
  font-weight: 400;
  padding-right: 1.2em;
  text-transform: capitalize;
}

.username,
.date {
  font-size: var(--body);
  color: var(--colour-text-light);
}

.useremail {
  font-size: var(--body);
  color: var(--colour-text-default);
  margin-top: 0.8em;
}

.button-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 0.8em;
}

.account-details :deep(.info-button) {
  filter: grayscale(1);
  min-width: unset;
  min-height: unset;
}

.account-details :deep(.info-button img) {
  width: 1.7em;
  height: 1.7em;
}
</style>
